/**
 * Google Identity Services: loading the script, and nothing else.
 *
 * GIS is the app's only third-party script and it is loaded on demand rather than from
 * `index.html`, so a signed-in player who reloads into their story does not block on
 * `accounts.google.com` before the first paint.
 */

export type GoogleIdentity = typeof google.accounts.id;

const GIS_SRC = 'https://accounts.google.com/gsi/client';

let pending: Promise<GoogleIdentity> | null = null;

/**
 * Resolves with `google.accounts.id`, injecting the script once and memoizing the promise —
 * StrictMode's double-effect, two components mounting at once and a remount after sign-out
 * all share one load.
 *
 * Rejects if the script cannot load (offline, blocked by an extension, CSP). The caller
 * turns that into a visible "sign-in is unavailable" state; it must never be a blank page.
 */
export function loadGoogleIdentity(): Promise<GoogleIdentity> {
  if (globalThis.google?.accounts?.id) {
    return Promise.resolve(globalThis.google.accounts.id);
  }
  pending ??= injectScript();
  return pending;
}

function injectScript(): Promise<GoogleIdentity> {
  return new Promise<GoogleIdentity>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${GIS_SRC}"]`);
    const script = existing ?? document.createElement('script');

    const onLoad = () => {
      const identity = globalThis.google?.accounts?.id;
      if (identity) {
        resolve(identity);
      } else {
        pending = null;
        reject(new Error('The Google Identity script loaded but exposed no accounts.id.'));
      }
    };
    const onError = () => {
      pending = null;
      reject(new Error('The Google Identity script could not be loaded.'));
    };

    script.addEventListener('load', onLoad, { once: true });
    script.addEventListener('error', onError, { once: true });

    if (!existing) {
      script.src = GIS_SRC;
      script.async = true;
      script.defer = true;
      document.head.append(script);
    }
  });
}

/** Test seam: forgets the memoized load so each test starts from a clean document. */
export function resetGoogleIdentity(): void {
  pending = null;
}
